import { CachePolicy } from 'contentstack';
import { unstable_cache } from 'next/cache';

import { logger } from '@/services/logger';
import { env } from '@/src/env';
import {
    ConfigStack,
    ContentStack,
} from '@nxt-costco-com/forge-digital-components';

import { CONFIGSTACK, CONFIGSTACK_LIVEPREVIEW } from './stackClient';

export const ConfigContentType = {
    ConfigurationSetting: 'Configuration_Setting',
    FeatureToggle: 'Feature_Toggle',
    ModuleSettings: '_module_settings',
    PageSettings: '_page_settings',
    ServiceConfiguration: 'Service_Configuration',
} as const;

export type ContentTypeProps =
    (typeof ConfigContentType)[keyof typeof ConfigContentType];

export async function getConfigurationSettingByEntryId<T>(
    contentType: ContentTypeProps,
    entryId: string,
    enableLivePreview?: boolean,
): Promise<T> {
    let stackClient = CONFIGSTACK;

    if (enableLivePreview) {
        stackClient = CONFIGSTACK_LIVEPREVIEW;
        stackClient.setCachePolicy(CachePolicy.IGNORE_CACHE);
    }

    const clientConfigurationLanguage =
        enableLivePreview &&
        process.env.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION
            ? process.env
                  .NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION
            : env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION;

    try {
        return await stackClient
            .ContentType(contentType)
            .Entry(entryId)
            .language(clientConfigurationLanguage)
            .toJSON()
            .fetch();
    } catch {
        throw new Error(
            `Error fetching config type: ${contentType} with entry id: ${entryId}`,
        );
    }
}

export const _getConfigurationSettingByDevelopmentKey = async <T>(
    contentType: ContentTypeProps,
    developmentKey: string,
    enableLivePreview?: boolean,
): Promise<T> => {
    let stackClient = CONFIGSTACK;

    if (enableLivePreview) {
        stackClient = CONFIGSTACK_LIVEPREVIEW;
        stackClient.setCachePolicy(CachePolicy.IGNORE_CACHE);
    }

    const clientConfigurationLanguage =
        enableLivePreview &&
        process.env.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION
            ? process.env
                  .NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION
            : env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION;

    try {
        logger.info(`Fetching Config Stack Settings - ${developmentKey}`);
        return await stackClient
            .ContentType(contentType)
            .Query()
            .where('development_key', developmentKey)
            .language(clientConfigurationLanguage)
            .toJSON()
            .findOne();
    } catch (err) {
        logger.error(
            { err },
            `Error fetching Config Stack Settings - ${developmentKey}`,
        );
        // If you don't rethrow, typescript will complain that the function does not return a value
        throw new Error(
            `An error occurred in function getConfigurationSettingByDevelopmentKey - ${developmentKey}: ${JSON.stringify(err, null, 2)}`,
        );
    }
};

const cached_getConfigurationSettingByDevelopmentKey = unstable_cache(
    _getConfigurationSettingByDevelopmentKey,
    ['search_config_getConfigurationSettingByDevelopmentKey'],
    { revalidate: 1800 },
);

export const getConfigurationSettingByDevelopmentKey = async <T>(
    contentType: ContentTypeProps,
    developmentKey: string,
    enableLivePreview?: boolean,
): Promise<T> => {
    const fn =
        typeof window === 'undefined'
            ? cached_getConfigurationSettingByDevelopmentKey
            : _getConfigurationSettingByDevelopmentKey;

    return fn(contentType, developmentKey, enableLivePreview);
};

export const _getContentStackDataByDevelopmentKey = async <T>(
    contentType: ContentTypeProps,
    developmentKey: string,
    locale: string,
): Promise<T> => {
    try {
        logger.info(`Fetching Content Stack Settings - ${developmentKey}`);
        return await ContentStack.stack
            .ContentType(contentType)
            .Query()
            .where('development_key', developmentKey)
            .language(locale)
            .toJSON()
            .findOne();
    } catch (err) {
        logger.error(
            { err },
            `Error fetching Content Stack Settings - ${developmentKey}`,
        );
        throw new Error(
            `An error ocurred in getContentStackDataByDevelopmentKey.
        contentType: ${contentType},
        developmentKey: ${developmentKey},
        locale: ${locale},
        ${JSON.stringify(err, null, 2)}`,
        );
    }
};

const cached_getContentStackDataByDevelopmentKey = unstable_cache(
    _getContentStackDataByDevelopmentKey,
    ['search_config_getContentStackDataByDevelopmentKey'],
    { revalidate: 1800 },
);

export const getContentStackDataByDevelopmentKey = async <T>(
    contentType: ContentTypeProps,
    developmentKey: string,
    locale: string,
): Promise<T> => {
    const fn =
        typeof window === 'undefined'
            ? cached_getContentStackDataByDevelopmentKey
            : _getContentStackDataByDevelopmentKey;

    return fn(contentType, developmentKey, locale);
};

export const _getConfigurationSettingByConfigKey = async <T>(
    contentType: ContentTypeProps,
    configKey: string,
): Promise<T> => {
    try {
        const result = await ConfigStack.stack
            .ContentType(contentType)
            .Query()
            .where('configkey', configKey)
            .language(
                ConfigStack.env
                    .CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION ?? '',
            )
            .toJSON()
            .findOne();
        if (process.env.LOG_CONTENTSTACK_CALLS === 'true') {
            logger.info(
                result,
                `Config stack data fetched successfully using getConfigurationSettingByConfigKey with contentType: ${contentType}, configKey: ${configKey}`,
            );
        }
        return result;
    } catch (err) {
        const errorString = `An error occurred in Config stack getConfigurationSettingByConfigKey with 
      contentType: ${contentType},
      configKey: ${configKey},
      resulting in error: ${typeof err === 'object' && JSON.stringify(err)}`;
        logger.error(
            err,
            `An error occurred in Config stack getConfigurationSettingByConfigKey with contentType: ${contentType}, configKey: ${configKey}`,
        );
        throw new Error(errorString);
    }
};

const cached_getConfigurationSettingByConfigKey = unstable_cache(
    _getConfigurationSettingByConfigKey,
    ['search_config_getConfigurationSettingByConfigKey'],
    { revalidate: 1800 },
);

export const getConfigurationSettingByConfigKey = async <T>(
    contentType: ContentTypeProps,
    configKey: string,
): Promise<T> => {
    const fn =
        typeof window === 'undefined'
            ? cached_getConfigurationSettingByConfigKey
            : _getConfigurationSettingByConfigKey;

    return fn(contentType, configKey);
};
